/**
 * SwatchSizeAvailability
 *
 * After the initial page render, fetches variant availability for every color
 * swatch in the product-grouping component and marks swatches that don't have
 * an available variant in the currently selected size and inseam with the class `is-soldout-in-size`.
 *
 * Reacts to:
 *  - clicks on size / inseam option buttons
 *  - `document` "variant:change"          — variant changed
 *  - `window`   "productGroupingInjected" — user clicked a color swatch or length
 *                                           and the new product HTML was injected
 */

const ACTIVE_OPTION_BTN =
  'button[js-update-variant].is-active, button[js-update-variant][aria-checked="true"]';

class SwatchSizeAvailability {
 constructor() {
    this._cache = new Map();
    this._timer = null;
    this._lastSelectionKey = null;
    this._checkGen = 0;
    this._pendingColorChange = false;
    this._userSelectedSize = false;

    this._bindEvents();

    // Run once on load if a size is already selected
    const initialCheck = () => {
      if (this._getActiveSizeButton()) {
        this._userSelectedSize = true;
        this._scheduleCheck(false, 0);
      }
    };

    if ('requestIdleCallback' in window) {
      requestIdleCallback(initialCheck, { timeout: 2000 });
    } else {
      setTimeout(initialCheck, 400);
    }
  }

  // ─── Events ───
  _bindEvents() {
    document.addEventListener(
      'click',
      (e) => {
        if (!e.isTrusted) return;
        // Any variant option row (size/waist, inseam, etc.)
        const optionBtn = e.target.closest(
          'product-grouping .product-grouping__options button[js-update-variant]'
        );
        if (!optionBtn) return;
        this._userSelectedSize = true;
        this._scheduleCheck(false);
      },
      true
    );

    document.addEventListener('variant:change', () => {
      if (this._userSelectedSize) this._scheduleCheck(false);
    });

    window.addEventListener('productGroupingInjected', () => {
      if (this._userSelectedSize) this._scheduleCheck(true);
    });
  }

  _scheduleCheck(colorChanged = false, delay = 80) {
    this._pendingColorChange = this._pendingColorChange || colorChanged;
    clearTimeout(this._timer);
    this._timer = setTimeout(() => {
      const wasColorChange = this._pendingColorChange;
      this._pendingColorChange = false;
      this._check(wasColorChange);
    }, delay);
  }

  // ─── DOM helpers ───
  // Color swatches only (Length pills also carry js-request-new-product)
  _getSwatchButtons() {
    return Array.from(
      document.querySelectorAll(
        'product-grouping [js-request-new-product][data-product-handle]:not([js-update-sub-option])'
      )
    );
  }

  _getActiveSizeButton() {
    return document.querySelector(
      'product-grouping .product-grouping__options.is-size button[js-update-variant].is-active, ' +
      'product-grouping .product-grouping__options.is-size button[js-update-variant][aria-checked="true"]'
    );
  }

  // Buttons store values without the inch mark (30), variants have it (30")
  _normalize(value) {
    return String(value ?? '').replace(/"/g, '').trim();
  }

  // Returns the selected non-color options, e.g. { option2: '32', option3: '30' }, or null if no size is selected.
  _getSelectedOptions() {
    const selection = {};

    document
      .querySelectorAll('product-grouping .product-grouping__options')
      .forEach((row) => {
        const active = row.querySelector(ACTIVE_OPTION_BTN);
        const index = active?.dataset.optionIndex;
        if (index && active.dataset.value) {
          selection[`option${index}`] = this._normalize(active.dataset.value);
        }
      });

    if (!selection.option2) {
      const anyActive = document.querySelector(
        'product-grouping button[js-update-variant].is-active'
      );
      if (!anyActive?.dataset.value) return null;
      selection.option2 = this._normalize(anyActive.dataset.value);
    }

    return selection;
  }

  _matchesSelection(variant, selection) {
    return Object.entries(selection).every(([key, value]) => {
      // Sibling color doesn't have this option (e.g. no inseam): don't filter on it
      if (variant[key] == null) return true;
      return this._normalize(variant[key]) === value;
    });
  }

  // ─── Data fetching ───
  async _fetchVariants(handle) {
    if (this._cache.has(handle)) return this._cache.get(handle);

    try {
      const res = await fetch(`/products/${encodeURIComponent(handle)}.js`);
      if (!res.ok) return null;
      const { variants } = await res.json();
      this._cache.set(handle, variants ?? []);
      return this._cache.get(handle);
    } catch {
      return null;
    }
  }

  // Returns null if the stock check fails, so callers can leave swatches alone
  async _fetchVariantsAvailabilityBatch(variantIds = []) {
    const ids = [...new Set((variantIds || []).map(String).filter(Boolean))];
    if (!ids.length) return {};

    const accessToken = window.__VPV?.shop?.storefrontToken;
    const store = window.__VPV?.shop?.name;
    const locale = window.__VPV?.shop?.locale || 'en-US';

    if (!accessToken || !store) return null;

    const country =
      typeof window.Shopify?.country === 'string' && /^[A-Z]{2}$/.test(window.Shopify.country)
        ? window.Shopify.country
        : null;
    const query = country
      ? `
      query GetVariantsAvailability($ids: [ID!]!, $country: CountryCode!) @inContext(country: $country) {
        nodes(ids: $ids) {
          ... on ProductVariant {
            id
            availableForSale
          }
        }
      }
    `
      : `
      query GetVariantsAvailability($ids: [ID!]!) {
        nodes(ids: $ids) {
          ... on ProductVariant {
            id
            availableForSale
          }
        }
      }
    `;
    const variables = {
      ids: ids.map((id) => `gid://shopify/ProductVariant/${id}`),
    };
    if (country) variables.country = country;

    try {
      const res = await fetch(
        `https://${store}.myshopify.com/api/2025-01/graphql.json`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': accessToken,
            Accept: 'application/json',
            'Accept-Language': locale,
          },
          body: JSON.stringify({
            query,
            variables,
          }),
        }
      );

      if (!res.ok) return null;

      const result = await res.json();
      if (!Array.isArray(result?.data?.nodes)) return null;

      const availability = {};

      for (const node of result.data.nodes) {
        if (!node?.id) continue;
        availability[node.id.split('/').pop()] = node.availableForSale === true;
      }

      return availability;
    } catch {
      return null;
    }
  }

  _isVariantSellableFromMap(variant, availabilityMap) {
    if (!variant?.id) return false;
    const sellable = availabilityMap[String(variant.id)];
    return sellable === true;
  }

  // ─── Availability check ──
  async _check(colorChanged = false) {
    if (!this._userSelectedSize) return;

    const swatches = this._getSwatchButtons();
    if (swatches.length === 0) return;

    const selection = this._getSelectedOptions();
    const selectionKey = selection ? JSON.stringify(selection) : null;

    if (!colorChanged && selectionKey === this._lastSelectionKey) return;
    this._lastSelectionKey = selectionKey;

    if (!selection) {
      swatches.forEach(btn => btn.classList.remove('is-soldout-in-size'));
      return;
    }

    const handles = [
      ...new Set(
        swatches
          .map(btn => btn.dataset.productHandle)
          .filter(Boolean)
      ),
    ];

    const gen = ++this._checkGen;

    const results = await Promise.all(
      handles.map(async handle => ({
        handle,
        variants: await this._fetchVariants(handle),
      }))
    );

    if (gen !== this._checkGen) return;

    const variantIdsForSelection = [];
    for (const { variants } of results) {
      if (!variants) continue;
      variants
        .filter((v) => this._matchesSelection(v, selection))
        .forEach((v) => variantIdsForSelection.push(v.id));
    }

    const availabilityMap = await this._fetchVariantsAvailabilityBatch(variantIdsForSelection);

    if (gen !== this._checkGen) return;

    // If the stock check fails, leave the colors alone
    if (!availabilityMap) {
      this._lastSelectionKey = null; // lets the next click try again
      return;
    }

    const available = {};
    for (const { handle, variants } of results) {
      if (!variants) continue;
      available[handle] = variants.some(
        (v) =>
          this._matchesSelection(v, selection) &&
          this._isVariantSellableFromMap(v, availabilityMap)
      );
    }

    // Look up the swatches again: the page may have re-rendered them while we were fetching
    // The active color is checked too, so choosing a crossed-out color keeps it crossed out
    this._getSwatchButtons().forEach(btn => {
      const handle = btn.dataset.productHandle;
      if (handle in available) {
        btn.classList.toggle('is-soldout-in-size', !available[handle]);
      }
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new SwatchSizeAvailability());
} else {
  new SwatchSizeAvailability();
}